/**
 * batch-70 voertuig-meldingen (FO-527, spec 227) - voertuigmutaties met
 * automatische melding aan de leasemaatschappij (Wassink), de correctie in
 * Cartracker en de loonstrookcorrectie aan de salarisverwerker (NAW).
 *
 * Eén bron: elke mutatie is een `VoertuigMutatieEvent`; per event en kanaal één
 * `VoertuigMeldingOpdracht`. Dit bestand bevat de gedeelde enums, labels, DTO's
 * en request-schema's (API + backoffice + portal).
 *
 * Nooit een BSN: de salarisverwerker herkent de medewerker aan personeelsnummer
 * en naam, en het voertuig aan kenteken en de NMBRS-referentie.
 */
import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import { VOERTUIG_TYPE, type VoertuigType } from './voertuigen'

/** `bronModule` van de handmatige Cartracker-taak en `module` van de meldingen. */
export const VOERTUIG_MELDING_BRON_MODULE = 'voertuig-meldingen'

// ─── Enums ───────────────────────────────────────────────────────────────────

export const VOERTUIG_MUTATIE_SOORT = [
  'BESTUURDER_TOEGEWEZEN',
  'BESTUURDER_GEWISSELD',
  'BESTUURDER_INGETROKKEN',
  'TIJDELIJK_GESTART',
  'TIJDELIJK_BEEINDIGD',
  'BIJTELLING_GEWIJZIGD',
  'VERKLARING_VASTGELEGD',
  'VERKLARING_INGETROKKEN',
  'VOERTUIG_IN_VLOOT',
  'VOERTUIG_UIT_VLOOT',
  'KENTEKEN_GEWIJZIGD',
  'LEASECONTRACT_GEWIJZIGD',
] as const
export type VoertuigMutatieSoort = (typeof VOERTUIG_MUTATIE_SOORT)[number]

export const VOERTUIG_MUTATIE_SOORT_LABELS: Record<VoertuigMutatieSoort, string> = {
  BESTUURDER_TOEGEWEZEN: 'Bestuurder toegewezen',
  BESTUURDER_GEWISSELD: 'Bestuurder gewisseld',
  BESTUURDER_INGETROKKEN: 'Bestuurder afgemeld',
  TIJDELIJK_GESTART: 'Tijdelijke bestuurder gestart',
  TIJDELIJK_BEEINDIGD: 'Tijdelijke bestuurder beëindigd',
  BIJTELLING_GEWIJZIGD: 'Bijtelling gewijzigd',
  VERKLARING_VASTGELEGD: 'Verklaring privégebruik vastgelegd',
  VERKLARING_INGETROKKEN: 'Verklaring privégebruik ingetrokken',
  VOERTUIG_IN_VLOOT: 'Voertuig in de vloot',
  VOERTUIG_UIT_VLOOT: 'Voertuig uit de vloot',
  KENTEKEN_GEWIJZIGD: 'Kenteken gewijzigd',
  LEASECONTRACT_GEWIJZIGD: 'Leasecontract gewijzigd',
}

export const VOERTUIG_MUTATIE_BRON = [
  'BACKOFFICE',
  'PORTAL',
  'ONBOARDING',
  'OFFBOARDING',
  'IMPORT',
  'CAMPAGNE',
  'SYSTEEM',
] as const
export type VoertuigMutatieBron = (typeof VOERTUIG_MUTATIE_BRON)[number]

export const VOERTUIG_MUTATIE_BRON_LABELS: Record<VoertuigMutatieBron, string> = {
  BACKOFFICE: 'Backoffice',
  PORTAL: 'Portal',
  ONBOARDING: 'Onboarding',
  OFFBOARDING: 'Offboarding',
  IMPORT: 'Import',
  CAMPAGNE: 'Bijtellingscampagne',
  SYSTEEM: 'Systeem',
}

export const VOERTUIG_MELDING_KANAAL = ['WASSINK', 'CARTRACKER', 'NAW'] as const
export type VoertuigMeldingKanaal = (typeof VOERTUIG_MELDING_KANAAL)[number]

export const VOERTUIG_MELDING_KANAAL_LABELS: Record<VoertuigMeldingKanaal, string> = {
  WASSINK: 'Leasemaatschappij (Wassink)',
  CARTRACKER: 'Cartracker',
  NAW: 'Salarisadministratie (NAW)',
}

export const VOERTUIG_MELDING_OPDRACHT_STATUS = [
  'GEPLAND',
  'VERSTUURD',
  'BEVESTIGD',
  'MISLUKT',
  'OVERGESLAGEN',
  'HANDMATIG',
] as const
export type VoertuigMeldingOpdrachtStatus = (typeof VOERTUIG_MELDING_OPDRACHT_STATUS)[number]

export const VOERTUIG_MELDING_OPDRACHT_STATUS_LABELS: Record<VoertuigMeldingOpdrachtStatus, string> = {
  GEPLAND: 'Gepland',
  VERSTUURD: 'Verstuurd',
  BEVESTIGD: 'Bevestigd',
  MISLUKT: 'Mislukt',
  OVERGESLAGEN: 'Overgeslagen',
  HANDMATIG: 'Handmatig gedaan',
}

/** Statussen waarin een opdracht nog werk vraagt of op een antwoord wacht. */
export const VOERTUIG_MELDING_OPEN_STATUSSEN: readonly VoertuigMeldingOpdrachtStatus[] = ['GEPLAND', 'VERSTUURD', 'MISLUKT']

/** Statussen waarin een opdracht klaar is (afgehandeld of bewust niet nodig). */
export const VOERTUIG_MELDING_AFGEROND_STATUSSEN: readonly VoertuigMeldingOpdrachtStatus[] = [
  'BEVESTIGD',
  'HANDMATIG',
  'OVERGESLAGEN',
]

export const VOERTUIG_CARTRACKER_MODUS = ['HANDMATIG', 'AUTOMATISCH'] as const
export type VoertuigCartrackerModus = (typeof VOERTUIG_CARTRACKER_MODUS)[number]

export const VOERTUIG_CARTRACKER_MODUS_LABELS: Record<VoertuigCartrackerModus, string> = {
  HANDMATIG: 'Handmatig (taak voor wagenparkbeheer)',
  AUTOMATISCH: 'Automatisch (API-koppeling)',
}

export const CLIENT_CONTACT_MELDING_ROL = ['AAN', 'CC'] as const
export type ClientContactMeldingRol = (typeof CLIENT_CONTACT_MELDING_ROL)[number]

export const CLIENT_CONTACT_MELDING_ROL_LABELS: Record<ClientContactMeldingRol, string> = {
  AAN: 'Ontvanger',
  CC: 'In kopie',
}

export const VOERTUIG_MELDING_AUDIT_ACTIE = [
  'GEPLAND',
  'OVERGESLAGEN',
  'VERSTUURD',
  'GEBUNDELD',
  'BEVESTIGD',
  'MISLUKT',
  'HANDMATIG',
  'OPNIEUW',
  'AKKOORD',
  'HERINNERING',
  'TAAK_AANGEMAAKT',
] as const
export type VoertuigMeldingAuditActie = (typeof VOERTUIG_MELDING_AUDIT_ACTIE)[number]

export const VOERTUIG_MELDING_AUDIT_ACTIE_LABELS: Record<VoertuigMeldingAuditActie, string> = {
  GEPLAND: 'Gepland',
  OVERGESLAGEN: 'Overgeslagen',
  VERSTUURD: 'Verstuurd',
  GEBUNDELD: 'Meegestuurd in een bundel',
  BEVESTIGD: 'Bevestigd',
  MISLUKT: 'Mislukt',
  HANDMATIG: 'Handmatig gedaan',
  OPNIEUW: 'Opnieuw ingepland',
  AKKOORD: 'Akkoord gegeven',
  HERINNERING: 'Herinnering verstuurd',
  TAAK_AANGEMAAKT: 'Taak aangemaakt',
}

/** Standaard NAW-regels: de soorten waarbij de loonstrook kan veranderen (§A2). */
export const VOERTUIG_MELDING_NAW_STANDAARD_SOORTEN: readonly VoertuigMutatieSoort[] = [
  'BESTUURDER_TOEGEWEZEN',
  'BESTUURDER_GEWISSELD',
  'BESTUURDER_INGETROKKEN',
  'TIJDELIJK_GESTART',
  'TIJDELIJK_BEEINDIGD',
  'BIJTELLING_GEWIJZIGD',
  'VERKLARING_VASTGELEGD',
  'VERKLARING_INGETROKKEN',
  'VOERTUIG_UIT_VLOOT',
]

// ─── DTO's ───────────────────────────────────────────────────────────────────

/** Welke acties de gebruiker op deze opdracht mag (server-side beslist). */
export interface VoertuigMeldingActiesDTO {
  opnieuw: boolean
  handmatig: boolean
  overslaan: boolean
  akkoord: boolean
  bevestigen: boolean
}

export interface VoertuigMeldingOpdrachtDTO {
  id: string
  kanaal: VoertuigMeldingKanaal
  status: VoertuigMeldingOpdrachtStatus
  voertuigId: string
  kenteken: string
  merkModel: string
  employeeId: string | null
  medewerkerNaam: string | null
  eventId: string
  soort: VoertuigMutatieSoort
  bron: VoertuigMutatieBron
  /** Ingangsdatum van de mutatie (yyyy-mm-dd, NL-kalenderdag). */
  ingangsdatum: string
  geplandOp: string
  verstuurdOp: string | null
  bevestigdOp: string | null
  bevestigingBron: string | null
  handmatigOp: string | null
  herinneringOp: string | null
  terugwerkend: boolean
  vereistAkkoord: boolean
  akkoordOp: string | null
  /** Meegestuurd in de melding van deze opdracht. */
  bundelLeiderId: string | null
  /** Aantal andere opdrachten dat in deze melding is meegestuurd. */
  gebundeldAantal: number
  taakId: string | null
  reden: string | null
  redenCode: string | null
  acties: VoertuigMeldingActiesDTO
  createdAt: string
}

export interface VoertuigMeldingKpiDTO {
  /** Verstuurd, nog geen bevestiging. */
  openBevestigingen: number
  /** Waarvan langer dan de termijn (herinnering verstuurd). */
  nietBevestigdNaTermijn: number
  mislukt: number
  wachtOpAkkoord: number
  gepland: number
  /** Cartracker-opdrachten met een open handmatige taak. */
  cartrackerOpen: number
}

export interface VoertuigMeldingLijstDTO {
  data: VoertuigMeldingOpdrachtDTO[]
  meta: { total: number; page: number; pageSize: number }
}

export interface VoertuigMeldingAuditRijDTO {
  id: string
  actie: VoertuigMeldingAuditActie
  vanStatus: VoertuigMeldingOpdrachtStatus | null
  naarStatus: VoertuigMeldingOpdrachtStatus | null
  toelichting: string | null
  actorNaam: string | null
  createdAt: string
}

export interface VoertuigMeldingDetailDTO extends VoertuigMeldingOpdrachtDTO {
  /** Leesbare regels over de mutatie (oud -> nieuw), zonder BSN. */
  mutatieRegels: string[]
  bevestigingNotitie: string | null
  /** Ontvangers van de mail (alleen voor WASSINK en NAW). */
  ontvangers: { to: string[]; cc: string[] } | null
  bundel: Array<{ id: string; soort: VoertuigMutatieSoort; ingangsdatum: string; status: VoertuigMeldingOpdrachtStatus }>
  audit: VoertuigMeldingAuditRijDTO[]
}

export interface VoertuigMeldingLeverancierDTO {
  id: string
  naam: string
  clientNumber: string
}

export interface VoertuigMeldingInstellingenDTO {
  wassinkActief: boolean
  wassinkVertragingMin: number
  wassinkLeveranciers: VoertuigMeldingLeverancierDTO[]
  wassinkCc: string[]
  wassinkAntwoordAdres: string | null
  cartrackerActief: boolean
  cartrackerModus: VoertuigCartrackerModus
  cartrackerVertragingMin: number
  cartrackerVoertuigTypes: VoertuigType[]
  cartrackerUitvoerderId: string | null
  cartrackerUitvoerderNaam: string | null
  /** Is er een Cartracker-API-koppeling? Zonder koppeling mislukt AUTOMATISCH. */
  cartrackerApiBeschikbaar: boolean
  nawActief: boolean
  nawVertragingMin: number
  nawSoorten: VoertuigMutatieSoort[]
  nawCc: string[]
  nawAntwoordAdres: string | null
  /** Het adres van de salarisverwerker staat bij Payroll > Loonstroken > Instellingen. */
  nawAdresIngesteld: boolean
  tijdelijkNawDrempelDagen: number
  terugwerkendMarkeringDagen: number
  terugwerkendAkkoordDagen: number
  bevestigingWerkdagen: number
  vangnetVanaf: string
  bijgewerktOp: string
  magBewerken: boolean
}

/** Portal: alleen de status, geen interne details (spec §E12). */
export interface MijnVoertuigMeldingStatusDTO {
  /** Laatste moment waarop alle kanalen geïnformeerd waren (ISO), of null. */
  geinformeerdOp: string | null
  wassink: 'GEINFORMEERD' | 'BEZIG' | 'NVT'
  salaris: 'GEINFORMEERD' | 'BEZIG' | 'NVT'
  /** Naam van de leasemaatschappij van het voertuig (of null: niet gekoppeld). */
  leasemaatschappijNaam: string | null
}

/** Eenmalige consistentielijst: bestuurder in de app vs laatst gemelde bestuurder. */
export interface VoertuigMeldingConsistentieRijDTO {
  voertuigId: string
  kenteken: string
  bestuurderApp: string | null
  laatstGemeldeBestuurder: string | null
  laatstGemeldOp: string | null
  klopt: boolean
}

// ─── Request-schema's ─────────────────────────────────────────────────────────

const ymdSchema = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Gebruik een datum in de vorm jjjj-mm-dd')

/**
 * De KPI-tegels als filter: de lijst toont precies wat de tegel telt (review L4).
 * Zelfde where-bouwer in de API voor tegel, lijst en export.
 */
export const VOERTUIG_MELDING_WEERGAVEN = [
  'OPEN_BEVESTIGINGEN',
  'HERINNERD',
  'MISLUKT',
  'WACHT_OP_AKKOORD',
  'CARTRACKER_OPEN',
] as const
export type VoertuigMeldingWeergave = (typeof VOERTUIG_MELDING_WEERGAVEN)[number]

/**
 * De reden-codes van een opdracht (overslaan, mislukken, plannen), voor het filter
 * op Mutatiemeldingen (integrator-review H1). Een code buiten deze lijst blijft
 * geldig in de data; het filter kiest uit deze.
 */
export const VOERTUIG_MELDING_REDEN_LABELS: Record<string, string> = {
  KANAAL_UIT: 'Kanaal stond uit',
  GEEN_LEASE: 'Geen leasevoertuig',
  LEASEMAATSCHAPPIJ_ONBEKEND: 'Leasemaatschappij onbekend',
  LEVERANCIER_ONTVANGT_NIET: 'Leasemaatschappij ontvangt niet',
  GEEN_TRACKER: 'Geen tracker',
  NIET_RELEVANT: 'Niet relevant voor dit kanaal',
  NAW_REGEL_UIT: 'Niet in de NAW-regels',
  GEEN_BESTUURDER: 'Geen medewerker',
  GEEN_AUTO_VAN_DE_ZAAK: 'Geen auto van de zaak',
  TIJDELIJK_KORT: 'Tijdelijk en kort',
  CAMPAGNE: 'Via de campagne',
  GEEN_WIJZIGING: 'Geen wijziging',
  GEVOLG_WISSEL: 'Gevolg van de wissel',
  VERVANGEN: 'Vervangen',
  HISTORIE_IMPORT: 'Historie uit een import',
  IMPORT_TERUGGEDRAAID: 'Import teruggedraaid',
  HEFT_OP: 'Mutaties heffen elkaar op',
  HANDMATIG_OVERGESLAGEN: 'Handmatig overgeslagen',
  TERUGWERKEND: 'Met terugwerkende kracht',
  AKKOORD_NODIG: 'Wacht op akkoord',
  GEEN_CONTACT: 'Geen contactpersoon',
  GEEN_ADRES: 'Geen adres salarisverwerker',
  GEEN_API: 'Geen Cartracker-koppeling',
  MAIL_NIET_VERSTUURD: 'Mail niet verstuurd',
  MAIL_FOUT: 'Mailfout',
  NIET_BEZORGD: 'Niet bezorgd',
  KEUZE_ONTBREEKT: 'Verklaring ontbreekt',
  MEDEWERKER_ONBEKEND: 'Medewerker onbekend',
  VOERTUIG_ONBEKEND: 'Voertuig onbekend',
  OPNIEUW: 'Opnieuw ingepland',
  VERLENGD: 'Tijdelijke bestuurder verlengd',
}

export const VOERTUIG_MELDING_WEERGAVE_LABELS: Record<VoertuigMeldingWeergave, string> = {
  OPEN_BEVESTIGINGEN: 'Open bevestigingen',
  HERINNERD: 'Herinnering verstuurd',
  MISLUKT: 'Mislukt',
  WACHT_OP_AKKOORD: 'Wacht op akkoord',
  CARTRACKER_OPEN: 'Cartracker-taak open',
}

export const voertuigMeldingLijstQuerySchema = z.object({
  kanaal: z.enum(VOERTUIG_MELDING_KANAAL).optional(),
  status: z.enum(VOERTUIG_MELDING_OPDRACHT_STATUS).optional(),
  soort: z.enum(VOERTUIG_MUTATIE_SOORT).optional(),
  /** Ingangsdatum vanaf/tot en met (yyyy-mm-dd). */
  vanaf: ymdSchema.optional(),
  tot: ymdSchema.optional(),
  voertuigId: idParam('voertuigId').optional(),
  employeeId: idParam('employeeId').optional(),
  /** Alleen opdrachten die nog werk vragen of op een antwoord wachten. */
  alleenOpen: queryBoolean().optional(),
  /** Precies wat een KPI-tegel telt. */
  weergave: z.enum(VOERTUIG_MELDING_WEERGAVEN).optional(),
  /** De reden van overslaan of mislukken (bv. KANAAL_UIT). */
  redenCode: z.string().regex(/^[A-Z_]{2,40}$/, 'Onbekende reden').optional(),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
})
export type VoertuigMeldingLijstQuery = z.infer<typeof voertuigMeldingLijstQuerySchema>

/** Bulk: de overgeslagen opdrachten (kanaal uit) van een kanaal opnieuw beoordelen. */
export const voertuigMeldingKanaalUitOpnieuwSchema = z.object({
  kanaal: z.enum(VOERTUIG_MELDING_KANAAL),
})
export type VoertuigMeldingKanaalUitOpnieuwInput = z.infer<typeof voertuigMeldingKanaalUitOpnieuwSchema>

export interface VoertuigMeldingKanaalUitOpnieuwDTO {
  /** Opnieuw ingepland (eventueel wachtend op akkoord). */
  gepland: number
  /** Opnieuw beoordeeld en met een eigen reden overgeslagen. */
  overgeslagen: number
  /** Er waren er meer dan in één keer: nog een keer kiezen. */
  meer: boolean
}

export const voertuigMeldingOverslaanSchema = z.object({
  reden: z.string().trim().min(3, 'Geef een reden van minimaal 3 tekens').max(500, 'Maximaal 500 tekens'),
})
export type VoertuigMeldingOverslaanInput = z.infer<typeof voertuigMeldingOverslaanSchema>

export const voertuigMeldingHandmatigSchema = z.object({
  notitie: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type VoertuigMeldingHandmatigInput = z.infer<typeof voertuigMeldingHandmatigSchema>

export const voertuigMeldingBevestigSchema = z.object({
  /** Wanneer de leasemaatschappij of salarisverwerker het verwerkte (yyyy-mm-dd); leeg = vandaag. */
  datum: ymdSchema.optional(),
  notitie: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type VoertuigMeldingBevestigInput = z.infer<typeof voertuigMeldingBevestigSchema>

export const voertuigMeldingAkkoordSchema = z.object({
  notitie: z.string().trim().max(500, 'Maximaal 500 tekens').optional(),
})
export type VoertuigMeldingAkkoordInput = z.infer<typeof voertuigMeldingAkkoordSchema>

const emailLijst = z
  .array(z.string().trim().toLowerCase().email('Geen geldig e-mailadres'))
  .max(10, 'Maximaal 10 adressen')

const vertraging = z.coerce
  .number()
  .int('Gebruik hele minuten')
  .min(0, 'Minimaal 0 minuten')
  .max(240, 'Maximaal 240 minuten')

export const voertuigMeldingInstellingenSchema = z
  .object({
    wassinkActief: z.boolean(),
    wassinkVertragingMin: vertraging,
    wassinkLeveranciers: z.array(idParam('leverancier')).max(10, 'Maximaal 10 leveranciers'),
    wassinkCc: emailLijst,
    wassinkAntwoordAdres: z.string().trim().toLowerCase().email('Geen geldig e-mailadres').nullable(),
    cartrackerActief: z.boolean(),
    cartrackerModus: z.enum(VOERTUIG_CARTRACKER_MODUS),
    cartrackerVertragingMin: vertraging,
    cartrackerVoertuigTypes: z.array(z.enum(VOERTUIG_TYPE)).min(1, 'Kies minstens één voertuigtype'),
    cartrackerUitvoerderId: idParam('uitvoerder').nullable(),
    nawActief: z.boolean(),
    nawVertragingMin: vertraging,
    nawSoorten: z.array(z.enum(VOERTUIG_MUTATIE_SOORT)),
    nawCc: emailLijst,
    nawAntwoordAdres: z.string().trim().toLowerCase().email('Geen geldig e-mailadres').nullable(),
    tijdelijkNawDrempelDagen: z.coerce.number().int().min(0, 'Minimaal 0 dagen').max(90, 'Maximaal 90 dagen'),
    terugwerkendMarkeringDagen: z.coerce.number().int().min(1, 'Minimaal 1 dag').max(365, 'Maximaal 365 dagen'),
    terugwerkendAkkoordDagen: z.coerce.number().int().min(1, 'Minimaal 1 dag').max(730, 'Maximaal 730 dagen'),
    bevestigingWerkdagen: z.coerce.number().int().min(1, 'Minimaal 1 werkdag').max(30, 'Maximaal 30 werkdagen'),
  })
  .superRefine((v, ctx) => {
    if (v.terugwerkendAkkoordDagen < v.terugwerkendMarkeringDagen) {
      ctx.addIssue({
        code: 'custom',
        path: ['terugwerkendAkkoordDagen'],
        message: 'Het akkoord geldt voor oudere mutaties dan de markering: kies een gelijk of hoger aantal dagen',
      })
    }
  })
export type VoertuigMeldingInstellingenInput = z.infer<typeof voertuigMeldingInstellingenSchema>

export const voertuigMeldingLeverancierQuerySchema = z.object({
  search: z.string().trim().max(100).optional(),
})
